import React from 'react';
import { LucideIcon, Loader2 } from 'lucide-react';
import { Button } from '@/ui-components/ui/button';
import { cn } from '@/lib/utils/cn';

interface PrimaryActionButtonProps {
  onClick?: () => void;
  icon?: LucideIcon;
  label: string;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  type?: "button" | "submit";
  variant?: "dark" | "blue";
}

/**
 * Reusable primary action button for toolbars and headers.
 * Design-coherent with PageInfoBanner and DataViewToolbar.
 */
export const PrimaryActionButton = React.forwardRef<HTMLButtonElement, PrimaryActionButtonProps>(({
  onClick,
  icon: Icon,
  label,
  className,
  disabled,
  isLoading,
  type = "button",
  variant = "dark"
}, ref) => {
  return (
    <Button
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      size="sm"
      className={cn(
        "h-9 px-4 text-sm font-bold transition-all active:scale-95 shadow-sm rounded-xl min-w-0",
        variant === "dark" 
          ? "bg-gray-900 text-white hover:bg-gray-800" 
          : "bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/10",
        className
      )}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin shrink-0 sm:mr-2" />
      ) : Icon ? (
        <Icon className="h-4 w-4 shrink-0 sm:mr-2" />
      ) : null}
      <span className={cn("truncate", (isLoading || !!Icon) && "hidden sm:inline")}>{label}</span>
    </Button>
  );
});

PrimaryActionButton.displayName = "PrimaryActionButton";
